import { useCallback, useMemo, useState } from 'react'
import { GraduationCap, BookText, FileBarChart, Plus } from 'lucide-react'
import type { AdminColumn } from '@/components/DataTable/types'
import { useToast } from '@/providers/UiProviders'
import { ModListScreen } from './ModListScreen'
import {
  makeFetcher,
  generateTransparency,
  MOD_STR as S,
  type TrainingRow,
  type PolicyRow,
  type TransparencyRow,
} from './modContentApi'

/**
 * A6.40 — O'quv rejimi. Yangi moderatorlar shadow qaror qabul qiladi (real
 * ta'sir YO'Q) — aniqlik o'lchanadi (GET /admin/moderation/training).
 */
export function TrainingModeScreen() {
  const columns = useMemo<AdminColumn<TrainingRow>[]>(
    () => [
      { key: 'id', header: 'ID', type: 'id' },
      { key: 'moderator', header: S.moderator, type: 'text' },
      { key: 'shadow_decisions', header: S.shadowDecisions, type: 'number' },
      { key: 'accuracy_pct', header: S.accuracy, type: 'number' },
      { key: 'status', header: S.status, type: 'text' },
      { key: 'started_at', header: S.startedAt, type: 'date' },
    ],
    [],
  )
  return (
    <ModListScreen<TrainingRow>
      screenId="A6.40"
      title={S.training}
      description={S.trainingDesc}
      columns={columns}
      fetcher={makeFetcher<TrainingRow>(
        '/admin/moderation/training',
        'items',
        (r) => `${r.moderator} ${r.status}`,
      )}
      rowId={(r) => String(r.id)}
      icon={<GraduationCap size={28} strokeWidth={1.5} />}
    />
  )
}

/**
 * A6.41 — Moderatsiya siyosati (ichki qo'llanma). Siyosat / qo'llanma hujjatlari
 * (GET /admin/moderation/policies).
 */
export function ModerationPolicyScreen() {
  const columns = useMemo<AdminColumn<PolicyRow>[]>(
    () => [
      { key: 'id', header: 'ID', type: 'id' },
      { key: 'title', header: S.title, type: 'text' },
      { key: 'category', header: S.category, type: 'text' },
      { key: 'version', header: S.version, type: 'text' },
      { key: 'updated_by', header: S.updatedBy, type: 'text' },
      { key: 'updated_at', header: S.updatedAt, type: 'date' },
    ],
    [],
  )
  return (
    <ModListScreen<PolicyRow>
      screenId="A6.41"
      title={S.policies}
      description={S.policiesDesc}
      columns={columns}
      fetcher={makeFetcher<PolicyRow>(
        '/admin/moderation/policies',
        'policies',
        (r) => `${r.title} ${r.category}`,
      )}
      rowId={(r) => String(r.id)}
      icon={<BookText size={28} strokeWidth={1.5} />}
    />
  )
}

/**
 * A6.42 — Shaffoflik hisoboti generatori. Davriy hisobotlar ro'yxati
 * (GET /admin/moderation/transparency) + generatsiya (POST ...).
 */
export function TransparencyReportScreen() {
  const { toast } = useToast()
  const [reloadKey, setReloadKey] = useState(0)
  const columns = useMemo<AdminColumn<TransparencyRow>[]>(
    () => [
      { key: 'id', header: 'ID', type: 'id' },
      { key: 'period', header: S.period, type: 'text' },
      { key: 'actions_total', header: S.actionsTotal, type: 'number' },
      { key: 'removals', header: S.removals, type: 'number' },
      { key: 'appeals', header: S.appeals, type: 'number' },
      { key: 'generated_by', header: S.generatedBy, type: 'text' },
      { key: 'status', header: S.status, type: 'text' },
      { key: 'generated_at', header: S.generatedAt, type: 'date' },
    ],
    [],
  )
  const fetcher = useMemo(
    () =>
      makeFetcher<TransparencyRow>(
        '/admin/moderation/transparency',
        'reports',
        (r) => `${r.period} ${r.generated_by}`,
      ),
    [reloadKey],
  )
  const generate = useCallback(async () => {
    const period = (window.prompt(S.periodPrompt) ?? '').trim()
    if (!period) return
    try {
      await generateTransparency(period)
      toast(S.reportQueued, { tone: 'success' })
      setReloadKey((k) => k + 1)
    } catch (e) {
      toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
    }
  }, [toast])
  return (
    <ModListScreen<TransparencyRow>
      screenId="A6.42"
      title={S.transparency}
      description={S.transparencyDesc}
      columns={columns}
      fetcher={fetcher}
      rowId={(r) => String(r.id)}
      icon={<FileBarChart size={28} strokeWidth={1.5} />}
      actions={
        <button
          onClick={() => void generate()}
          className="inline-flex items-center gap-1.5 rounded-md bg-accent px-4 py-1.5 text-sm font-medium text-white hover:bg-accent-hover"
        >
          <Plus size={14} />
          {S.generate}
        </button>
      }
    />
  )
}
